import cn from "classnames";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DELETE_BOARD_ITEM_MODAL,
  MOVE_CARDS_MODAL,
} from "../../constants/modal.constant";
import { CARD_PRODUCT } from "../../constants/internalModal.constant";
import { BOARD_CARD_LOADING } from "../../constants/loading.constant";
import { Errors } from "../../constants/errors";
import { useBoardCore } from "../../cores/boards";
import {
  CardStatus,
  CardStatusLabels,
} from "../../cores/boards/constants/card-type";
import { BoardType } from "../../cores/boards/constants/board-type.constant";
import { PutCardRequest } from "../../cores/boards/interfaces/api/put-card";
import { useCompanyCore } from "../../cores/company";
import { useGlobalCore } from "../../cores/globals";
import { Position } from "../../cores/globals/interfaces/globalEntity";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import AreYouSure from "../component/AreYouSure";
import BoardSideModals from "../component/boards/BoardSideModals";
import ActivityContainer from "../component/boards/creator/ActivityContainer";
import AttachmentContainer from "../component/boards/creator/AttachmentContainer";
import CardOptions from "../component/boards/creator/CardOptions";
import DescriptionContainer from "../component/boards/creator/DescriptionContainer";
import TextInput from "../component/boards/creator/TextInput";
import EmailCard from "../component/boards/creator/EmailCard";
import QuoteCard from "../component/boards/creator/QuoteCard";
import { TaskRow } from "../component/boards/creator/TaskRow";
import EventCard from "../component/boards/creator/EventCard";
import MoveCards from "../component/boards/modal/MoveCards";
import CompanySideModals from "../component/contactCompanies/CompanySideModals";
import ContactSideModals from "../component/contacts/ContactSideModals";
import EmailSideModals from "../component/gmail/EmailSideModals";
import EventSideModals from "../component/event/EventSideModals";
import TaskSideModals from "../component/tasks/TaskSideModals";
import InternalModal from "../layout/InternalModal";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import "./OpportunityDetail.scss";

type DetailTab =
  | "overview"
  | "activity"
  | "tasks"
  | "emails"
  | "quotes"
  | "events"
  | "files";

export const OpportunityDetailScreen = () => {
  const intl = useIntl();
  const { boardId, listId, cardId } = useParams<{
    boardId: string;
    listId: string;
    cardId: string;
  }>();
  const { company, user, isAdmin, isReseller, companyRole } = useMeCore();
  const { getCompany } = useCompanyCore();
  const { goToBusinessBoard, goToEditBoard } = useRouterCore();
  const {
    turnOnModal,
    turnOffModal,
    isModal,
    getModalContent,
    getModalVisibility,
    isInternalModal,
    turnOffInternalModal,
    getInternalModalContent,
    isLoading,
  } = useGlobalCore();
  const {
    card,
    boardBusiness,
    comments,
    emails,
    tasks,
    events,
    quotes,
    activities,
    lists,
    errorCode,
    getCard,
    getLists,
    getCustomFields,
    getCardTags,
    getBusinessBoard,
    putCard,
    postComment,
    postTagging,
    deleteCard,
    getFilters,
    getListBudgets,
    cleanCard,
  } = useBoardCore();

  const [activeTab, setActiveTab] = useState<DetailTab>("overview");
  const [editTitle, setEditTitle] = useState(false);
  const [title, setTitle] = useState("");
  const [ready, setReady] = useState(false);

  const board = boardBusiness;

  useEffect(() => {
    if (!company?.id || !boardId || !listId || !cardId) return;
    let cancelled = false;
    setReady(false);
    (async () => {
      await Promise.all([
        getBusinessBoard(company.id, boardId, getFilters(boardId), false),
        getLists(company.id, boardId),
        getCustomFields(company.id, boardId),
        getCardTags(company.id, boardId),
        getCompany(company.id),
        getCard(company.id, boardId, listId, cardId, true),
      ]);
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
      cleanCard();
    };
  }, [
    company?.id,
    boardId,
    listId,
    cardId,
    getBusinessBoard,
    getFilters,
    getLists,
    getCustomFields,
    getCardTags,
    getCompany,
    getCard,
    cleanCard,
  ]);

  useEffect(() => {
    setTitle(card?.title || "");
  }, [card?.title]);

  const canModify = useMemo(
    () =>
      isAdmin ||
      isReseller ||
      companyRole?.owner ||
      companyRole?.admin ||
      board?.userRoles?.find((role) => role.user.id === user?.id)?.owner ||
      card?.assignments?.findIndex((usr) => usr.id === user?.id) !== -1 ||
      card?.assignments?.length === 0,
    [isAdmin, isReseller, user, card, companyRole, board]
  );

  const stageName = useMemo(() => {
    const fromLists = lists?.find((list) => list.id === card?.list)?.name;
    if (fromLists) return fromLists;
    return board?.lists?.find((list) => list.id === card?.list)?.name || "—";
  }, [lists, board, card?.list]);

  const statusClass = useMemo(() => {
    if (card?.status === CardStatus.Won) return "opportunity-detail__badge--won";
    if (card?.status === CardStatus.Lost) return "opportunity-detail__badge--lost";
    return "opportunity-detail__badge--open";
  }, [card?.status]);

  const onUpdateSubmit = useCallback(
    (body: PutCardRequest, close = true, users?: string[]) => {
      if (!company?.id || !board?.id || !card) return;
      putCard(company.id, board.id, card.list, card.id, body, () => {
        if (users && users.length > 0 && body.description) {
          postTagging(
            company.id,
            board.id,
            card.list,
            card.id,
            users,
            body.description
          );
        }
        getCard(company.id, board.id, card.list, card.id);
      });

      if (
        body.budget ||
        body.currency ||
        body.status === CardStatus.Lost ||
        body.status === CardStatus.Oppened ||
        body.status === CardStatus.Won
      ) {
        const filter = getFilters(board.id);
        getListBudgets(company.id, board.id, card.list, filter);
      }

      if (close) setEditTitle(false);
    },
    [
      company,
      board,
      card,
      putCard,
      postTagging,
      getCard,
      getListBudgets,
      getFilters,
    ]
  );

  const onAddCommentSubmit = useCallback(
    (text: string, type: number, users: string[]) => {
      if (!company?.id || !board?.id || !card) return;
      postComment(company.id, board.id, card.list, card.id, text, type, () => {
        if (users.length > 0) {
          postTagging(
            company.id,
            board.id,
            card.list,
            card.id,
            users,
            text
          );
        }
        getCard(company.id, board.id, card.list, card.id);
      });
    },
    [company, board, card, postComment, postTagging, getCard]
  );

  const onDeleteCard = useCallback(() => {
    if (!company?.id || !board?.id || !card) return;
    turnOnModal(
      DELETE_BOARD_ITEM_MODAL,
      <AreYouSure
        message={`${intl.formatMessage({ id: "app.crmBusiness.delete?" })} ${
          card.title
        }?`}
        onSubmit={() => {
          deleteCard(company.id, board.id, card.list, card.id, () => {
            turnOffModal(DELETE_BOARD_ITEM_MODAL);
            if (board.type === BoardType.ACTIVITY) {
              goToEditBoard(board.id);
            } else {
              goToBusinessBoard();
            }
          });
        }}
        onClose={() => turnOffModal(DELETE_BOARD_ITEM_MODAL)}
      />,
      Position.LEFT
    );
  }, [
    turnOnModal,
    turnOffModal,
    company,
    board,
    card,
    deleteCard,
    intl,
    goToBusinessBoard,
    goToEditBoard,
  ]);

  const onOpeningMoveCards = useCallback(async () => {
    if (!company?.id || !board?.id || !card) return;
    const listNames = await getLists(company.id, board.id);
    let suggested: any[] = [];
    if (listNames) {
      const listIndex = listNames.findIndex((lst) => lst.id === card.list);
      suggested = [-3, -2, -1, 1, 2, 3].reduce((prev: any[], curr) => {
        const diff = listIndex + curr;
        if (diff >= 0 && diff < listNames.length) {
          return [
            ...prev,
            {
              ...listNames[diff],
              right: curr > 0,
            },
          ];
        }
        return prev;
      }, []);
    }
    turnOnModal(
      MOVE_CARDS_MODAL,
      <MoveCards card={card} board={board} suggested={suggested} />
    );
  }, [turnOnModal, getLists, company, card, board]);

  const goBack = useCallback(() => {
    if (board?.type === BoardType.ACTIVITY) {
      goToEditBoard(board.id);
      return;
    }
    goToBusinessBoard();
  }, [board, goToBusinessBoard, goToEditBoard]);

  const loading = isLoading(BOARD_CARD_LOADING) || !ready;
  const loadError = errorCode === Errors.CardLoad;

  if (loading && !card) {
    return (
      <Layout>
        <Content>
          <div className="opportunity-detail__loading">
            <FormattedMessage id="app.analytics.summary.loadingFetching" />
          </div>
        </Content>
      </Layout>
    );
  }

  if (loadError || !card) {
    return (
      <Layout>
        <Content>
          <div className="opportunity-detail__error">
            <h2>
              <FormattedMessage id="app.crmBusiness.fullDetail.notFound" />
            </h2>
            <Button color="primary" className="mt-3" onClick={goBack}>
              <FormattedMessage id="app.crmBusiness.fullDetail.backToBoard" />
            </Button>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content>
        <div className="opportunity-detail">
          <InternalModal
            active={isInternalModal(CARD_PRODUCT)}
            onClose={() => turnOffInternalModal(CARD_PRODUCT)}
          >
            {getInternalModalContent(CARD_PRODUCT)}
          </InternalModal>

          <div className="opportunity-detail__header">
            <div className="opportunity-detail__header-top">
              <div className="opportunity-detail__header-main">
                <button
                  type="button"
                  className="btn btn-link p-0 mb-2 text-muted"
                  onClick={goBack}
                >
                  <FontAwesomeIcon icon={["fas", "arrow-left"]} className="mr-2" />
                  <FormattedMessage id="app.crmBusiness.fullDetail.backToBoard" />
                </button>
                <div className="opportunity-detail__id">#{card.cardId}</div>
                {!editTitle ? (
                  <h1
                    className={cn("opportunity-detail__title", {
                      pointer: canModify,
                    })}
                    onClick={() => canModify && setEditTitle(true)}
                  >
                    {title}
                  </h1>
                ) : (
                  <TextInput
                    value={title}
                    className="mb-2"
                    onSubmit={(nextTitle) => onUpdateSubmit({ title: nextTitle })}
                    toggleEdition={() => setEditTitle(false)}
                    showChek={true}
                  />
                )}
                <div className="opportunity-detail__meta">
                  <span className={cn("opportunity-detail__badge", statusClass)}>
                    {intl.formatMessage({
                      id:
                        CardStatusLabels[`${card.status}`] ||
                        "app.opportunity.cardStatus.open",
                    })}
                  </span>
                  <span className="opportunity-detail__badge">{stageName}</span>
                  {!canModify && (
                    <span className="opportunity-detail__badge">
                      {intl.formatMessage({ id: "app.crmBusiness.readOnly" })}
                    </span>
                  )}
                  {card.tags?.map((tag) => (
                    <Badge
                      key={tag.id}
                      pill
                      className="px-2"
                      style={{
                        backgroundColor: tag.backgroundColor,
                        color: tag.textColor,
                      }}
                    >
                      {tag.name}
                    </Badge>
                  ))}
                </div>
                {(card.user || card.createdByAutomation) && (
                  <div className="opportunity-detail__created">
                    {card.user &&
                      intl.formatMessage(
                        { id: "app.crmBusiness.createdBy" },
                        {
                          user: `${card.user.firstName} ${card.user.lastName}`,
                          date: moment(card.createdAt).format("DD/MM/yyyy HH:mm"),
                        }
                      )}
                    {card.createdByAutomation &&
                      intl.formatMessage(
                        { id: "app.crmBusiness.createdByAutomation" },
                        {
                          date: moment(card.createdAt).format("DD/MM/yyyy HH:mm"),
                        }
                      )}
                  </div>
                )}
              </div>

              <div className="opportunity-detail__header-aside">
                <div className="opportunity-detail__pulse">
                  <div className="opportunity-detail__pulse-item">
                    <span>
                      <FormattedMessage id="app.crmBusiness.fullDetail.tasks" />
                    </span>
                    <strong>{tasks?.length || 0}</strong>
                  </div>
                  <div className="opportunity-detail__pulse-item">
                    <span>
                      <FormattedMessage id="app.crmBusiness.fullDetail.emails" />
                    </span>
                    <strong>{emails?.length || 0}</strong>
                  </div>
                  <div className="opportunity-detail__pulse-item">
                    <span>
                      <FormattedMessage id="app.crmBusiness.fullDetail.quotes" />
                    </span>
                    <strong>{quotes?.length || 0}</strong>
                  </div>
                </div>
                {canModify && (
                  <div className="opportunity-detail__actions">
                    <Button color="primary" outline onClick={onOpeningMoveCards}>
                      <FormattedMessage id="app.crmBusiness.moveCards.title" />
                    </Button>
                    <Button color="danger" outline onClick={onDeleteCard}>
                      <FormattedMessage id="app.activity.delete" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="opportunity-detail__layout">
            <aside className="opportunity-detail__panel opportunity-detail__sidebar">
              <div className="opportunity-detail__sidebar-header">
                <div className="opportunity-detail__panel-title">
                  <FormattedMessage id="app.crmBusiness.fullDetail.properties" />
                </div>
              </div>
              <CardOptions
                canModify={!!canModify}
                board={board}
                layout="stacked"
                onUpdateSubmit={onUpdateSubmit}
              />
            </aside>

            <section className="opportunity-detail__panel opportunity-detail__main">
              <Nav tabs className="opportunity-detail__main-tabs mb-3">
                {(
                  [
                    ["overview", "app.crmBusiness.fullDetail.tab.overview"],
                    ["activity", "app.crmBusiness.fullDetail.tab.activity"],
                    ["tasks", "app.crmBusiness.fullDetail.tab.tasks"],
                    ["emails", "app.crmBusiness.fullDetail.tab.emails"],
                    ["quotes", "app.crmBusiness.fullDetail.tab.quotes"],
                    ["events", "app.crmBusiness.fullDetail.tab.events"],
                    ["files", "app.crmBusiness.fullDetail.tab.files"],
                  ] as [DetailTab, string][]
                ).map(([id, labelId]) => (
                  <NavItem key={id}>
                    <NavLink
                      className={cn("pointer", { active: activeTab === id })}
                      onClick={() => setActiveTab(id)}
                    >
                      <FormattedMessage id={labelId} />
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="overview">
                  <DescriptionContainer
                    canModify={!!canModify}
                    type={board?.type!}
                    onSubmit={(description, users) =>
                      onUpdateSubmit({ description }, true, users)
                    }
                  />
                  <div className="mt-3">
                    <div className="opportunity-detail__panel-title">
                      <FormattedMessage id="app.crmBusiness.fullDetail.recentActivity" />
                    </div>
                    <ActivityContainer
                      board={board?.id!}
                      comments={(comments || []).slice(0, 5)}
                      emails={(emails || []).slice(0, 3)}
                      tasks={(tasks || []).slice(0, 5)}
                      events={(events || []).slice(0, 3)}
                      activities={(activities || []).slice(0, 5)}
                      quotes={(quotes || []).slice(0, 3)}
                      onSubmit={onAddCommentSubmit}
                    />
                  </div>
                </TabPane>

                <TabPane tabId="activity">
                  <ActivityContainer
                    board={board?.id!}
                    comments={comments || []}
                    emails={emails || []}
                    tasks={tasks || []}
                    events={events || []}
                    activities={activities || []}
                    quotes={quotes || []}
                    onSubmit={onAddCommentSubmit}
                  />
                </TabPane>

                <TabPane tabId="tasks">
                  {(tasks || []).length === 0 ? (
                    <div className="opportunity-detail__empty">
                      <FormattedMessage id="app.crmBusiness.fullDetail.empty.tasks" />
                    </div>
                  ) : (
                    (tasks || []).map((task) => (
                      <TaskRow key={task.id} task={task} board={board?.id!} />
                    ))
                  )}
                </TabPane>

                <TabPane tabId="emails">
                  {(emails || []).length === 0 ? (
                    <div className="opportunity-detail__empty">
                      <FormattedMessage id="app.crmBusiness.fullDetail.empty.emails" />
                    </div>
                  ) : (
                    (emails || []).map((email) => (
                      <EmailCard key={email.id} email={email} />
                    ))
                  )}
                </TabPane>

                <TabPane tabId="quotes">
                  {(quotes || []).length === 0 ? (
                    <div className="opportunity-detail__empty">
                      <FormattedMessage id="app.crmBusiness.fullDetail.empty.quotes" />
                    </div>
                  ) : (
                    (quotes || []).map((quote) => (
                      <QuoteCard key={quote.id} quote={quote} />
                    ))
                  )}
                </TabPane>

                <TabPane tabId="events">
                  {(events || []).length === 0 ? (
                    <div className="opportunity-detail__empty">
                      <FormattedMessage id="app.crmBusiness.fullDetail.empty.events" />
                    </div>
                  ) : (
                    (events || []).map((event) => (
                      <EventCard
                        key={event.id}
                        board={board?.id!}
                        event={event}
                      />
                    ))
                  )}
                </TabPane>

                <TabPane tabId="files">
                  <AttachmentContainer canModify={!!canModify} board={board} />
                </TabPane>
              </TabContent>
            </section>
          </div>
        </div>

        <TaskSideModals />
        <ContactSideModals />
        <CompanySideModals />
        <BoardSideModals />
        <EventSideModals />
        <EmailSideModals />

        <SideModal
          active={isModal(MOVE_CARDS_MODAL)}
          visible={getModalVisibility(MOVE_CARDS_MODAL)}
          onClose={() => turnOffModal(MOVE_CARDS_MODAL)}
        >
          {getModalContent(MOVE_CARDS_MODAL)}
        </SideModal>
        <SideModal
          active={isModal(DELETE_BOARD_ITEM_MODAL)}
          visible={getModalVisibility(DELETE_BOARD_ITEM_MODAL)}
          onClose={() => turnOffModal(DELETE_BOARD_ITEM_MODAL)}
        >
          {getModalContent(DELETE_BOARD_ITEM_MODAL)}
        </SideModal>
      </Content>
    </Layout>
  );
};

export default OpportunityDetailScreen;
